/**
 * Onboarding Flow - Pure Data Definition
 *
 * This flow guides users through:
 * 1. Creating at least one custom role (in addition to auto-created admin role)
 * 2. Inviting at least one team member
 *
 * This is pure data - no API calls, no business logic.
 * No dependencies on status checking or requirements.
 */

export const onboardingFlow = {
  id: 'onboarding_flow',
  name: 'Onboarding: Setup Roles & Invitations',
  description: 'Guide new users to create roles and invite team members',

  definition: {
    flows: {
      // FLOW 1: Initial Check on Login
      initial_assessment: {
        id: 'initial_assessment',
        name: 'First Login Assessment',
        description: 'Check user setup status (greeting shown separately by AssistantManager)',
        stageId: 'initial_check',
        steps: [
          {
            order: 1,
            action: 'check',
            id: 'check_roles_exist',
            title: 'Check if Custom Role Created',
            description: 'Call API to check if user has created at least one custom role (besides auto-created admin role)',
            check: {
              endpoint: 'GET /roles',
              condition: 'array.length >= 2',
              branches: {
                true: 'check_users_invited',
                false: 'show_role_form_fields', // Transition to create_role_flow's first step
              },
            },
          },
          {
            order: 2,
            action: 'check',
            id: 'check_users_invited',
            title: 'Check if Users Invited',
            description: 'Call GET /users and filter by invitedBy === currentUser.id, check if any exist',
            check: {
              endpoint: 'GET /users (then filter client-side by invitedBy)',
              condition: 'filtered_array.length > 0',
              branches: {
                true: 'onboarding_complete', // All setup done
                false: 'show_invite_form', // Transition to invite_user_flow's first step
              },
            },
          },
          {
            order: 3,
            action: 'guide',
            id: 'onboarding_complete',
            title: 'All Setup Complete',
            description: 'Congratulate user on completing onboarding',
            guide: {
              promptId: 'setup_complete_congratulations',
              format: 'markdown',
            },
            nextStep: undefined, // End of flow
          },
        ],
        exitConditions: [
          {
            type: 'completed',
            action: 'proceed_to_next_flow',
          },
        ],
      },

      // FLOW 2: No Roles Exist - AI Conversation to Create Role (Simplified)
      create_role_flow: {
        id: 'create_role_flow',
        name: 'Create Your First Role',
        description: 'AI guides user through creating their first role conversationally',
        stageId: 'role_creation',
        steps: [
          {
            order: 1,
            action: 'ai_form',
            id: 'show_role_form_fields',
            title: 'Create Role with AI',
            description: 'AI asks conversational questions to collect role details',
            ai_form: {
              metadataKey: 'create_role_form',
              endpoint: '/roles',
              method: 'POST',
            },
            nextStep: 'confirm_role_created',
          },
          {
            order: 2,
            action: 'guide',
            id: 'confirm_role_created',
            title: 'Role Created Successfully',
            description: 'Celebrate and prepare for next step',
            guide: {
              promptId: 'role_creation_milestone',
              format: 'text',
            },
            nextStep: 'show_invite_form',
          },
        ],
        exitConditions: [
          {
            type: 'completed',
            action: 'move_to_user_invitation_flow',
          },
          {
            type: 'cancelled',
            action: 'show_must_create_role_message',
          },
        ],
      },

      // FLOW 3: No Users Invited - Guide to Invite User
      invite_user_flow: {
        id: 'invite_user_flow',
        name: 'Invite Your First Team Member',
        description: 'Guide user through inviting their first team member',
        stageId: 'user_invitation',
        steps: [
          {
            order: 1,
            action: 'ai_form',
            id: 'show_invite_form',
            title: 'Invite Team Member with AI',
            description: 'AI asks conversational questions to collect user invitation details',
            ai_form: {
              metadataKey: 'invite_user_form',
              endpoint: '/users/invite',
              method: 'POST',
            },
            nextStep: 'confirm_user_invited',
          },
          {
            order: 2,
            action: 'guide',
            id: 'confirm_user_invited',
            title: 'Invitation Sent Successfully',
            description: 'Celebrate and complete setup',
            guide: {
              promptId: 'setup_complete_congratulations',
              format: 'markdown',
            },
            nextStep: undefined,
          },
        ],
        exitConditions: [
          {
            type: 'completed',
            action: 'show_completion_message',
          },
          {
            type: 'cancelled',
            action: 'show_must_invite_user_message',
          },
        ],
      },
    },

    forms: {
      create_role_form: {
        id: 'create_role_form',
        name: 'Create Role',
        description:
          'Form for creating a new role - The modal with role name, description, and permissions',
        fields: [
          {
            name: 'roleName',
            type: 'text',
            label: 'Role Name',
            placeholder: 'e.g., Data Manager, ESG Coordinator',
            required: true,
            helpText: 'Choose a descriptive name for this role',
            example: 'ESG Data Collector',
            validation: {
              minLength: 3,
              maxLength: 100,
            },
          },
          {
            name: 'description',
            type: 'textarea',
            label: 'Description',
            placeholder: 'Describe the role\'s purpose and responsibilities...',
            required: false,
            helpText: 'Help team members understand what this role does',
            example: 'Responsible for collecting and verifying environmental data',
          },
          {
            name: 'permissions',
            type: 'checkbox',
            label: 'Permissions',
            required: true,
            helpText:
              'Select what this role can do in the system (View data, Edit data, Approve, Generate reports)',
          },
        ],
        validationRules: [
          {
            field: 'roleName',
            rule: 'required',
            errorMessage: 'Role name is required',
          },
          {
            field: 'roleName',
            rule: 'unique',
            errorMessage: 'A role with this name already exists',
          },
          {
            field: 'permissions',
            rule: 'atLeastOne',
            errorMessage: 'You must select at least one permission',
          },
        ],
        submitButton: {
          text: 'Create Role',
          successMessage: '✅ Role created successfully! Now let\'s invite your first team member.',
        },
      },

      invite_user_form: {
        id: 'invite_user_form',
        name: 'Invite User',
        description:
          'Form for inviting a team member - Opens when clicking "Invite User" button on Team & Permissions page',
        fields: [
          {
            name: 'email',
            type: 'email',
            label: 'Email Address',
            placeholder: 'john.doe@company.com',
            required: true,
            helpText: 'The email address of the person you want to invite',
            validation: {
              pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
            },
          },
          {
            name: 'firstName',
            type: 'text',
            label: 'First Name',
            placeholder: 'John',
            required: true,
            helpText: 'Their first name',
          },
          {
            name: 'lastName',
            type: 'text',
            label: 'Last Name',
            placeholder: 'Doe',
            required: true,
            helpText: 'Their last name',
          },
          {
            name: 'jobTitle',
            type: 'text',
            label: 'Job Title',
            placeholder: 'ESG Manager',
            required: false,
            helpText: 'Their position in your organization',
          },
          {
            name: 'role',
            type: 'select',
            label: 'Assign Role',
            required: true,
            helpText: 'Choose which role to assign them - must select the role created earlier',
          },
        ],
        validationRules: [
          {
            field: 'email',
            rule: 'required',
            errorMessage: 'Email is required',
          },
          {
            field: 'email',
            rule: 'valid_email',
            errorMessage: 'Please enter a valid email address',
          },
          {
            field: 'firstName',
            rule: 'required',
            errorMessage: 'First name is required',
          },
          {
            field: 'lastName',
            rule: 'required',
            errorMessage: 'Last name is required',
          },
          {
            field: 'role',
            rule: 'required',
            errorMessage: 'You must assign a role',
          },
        ],
        submitButton: {
          text: 'Send Invitation',
          successMessage: '🎉 Invitation sent! Your team member will receive an email to join.',
        },
      },
    },

    aiPrompts: {
      welcome_first_login: {
        id: 'welcome_first_login',
        context: 'First time user logs in',
        variants: [
          `Welcome to ESG Caffe! 👋 I'm ESG Sense, your AI assistant for ESG reporting. I'm here to help you get started with just a few quick setup steps. Let me check what we need to do first...`,
          `Hi there! 🌱 Welcome to ESG Caffe. I'm ESG Sense. Let me assess where you are in the setup process and guide you through the next steps.`,
          `Great to see you! 👨‍💼 I'm ESG Sense. Let me check your setup status and help you get ready to start collecting ESG data.`,
        ],
      },

      explain_roles_importance: {
        id: 'explain_roles_importance',
        context: 'When user has not created a custom role',
        variants: [
          `## 🔑 Create Your First Custom Role

I see you have the default **Admin** role, but you haven't created a custom role yet. Custom roles are important because they allow you to give team members specific permissions based on their responsibilities.

**Examples of useful roles:**
- **ESG Manager** - Can manage data and generate reports
- **Data Collector** - Can enter environmental and social data
- **Reviewer** - Can review and approve submitted data
- **Analyst** - Read-only access to dashboards and analytics

**Let's create your first custom role!** Click the button below to go to Team & Permissions.`,
        ],
      },

      guide_create_role_form: {
        id: 'guide_create_role_form',
        context: 'Guiding user through role creation form',
        variants: [
          `## 📝 Create Your Custom Role

Now let's fill in the role details. This will be your first custom role (you already have the default Admin role):

1. **Role Name**: Give this role a clear name (e.g., "ESG Data Manager", "Environmental Officer", "Data Collector")
2. **Description**: Briefly describe what this role does (optional but helpful)
3. **Permissions**: Choose what this role can do:
   - View data ✓
   - Edit/Create data
   - Approve/Review
   - Generate reports

**Tip**: Start with a role that matches your main ESG responsibility.`,
        ],
      },

      role_created_success: {
        id: 'role_created_success',
        context: 'After user successfully creates a role',
        variants: [
          `✅ **Excellent!** You've created your first custom role.

**Next:** Let's invite your first team member so they can help with ESG data collection. This is important because ESG reporting requires data from different departments (HR for social, Finance for environmental, etc.).`,
        ],
      },

      role_creation_milestone: {
        id: 'role_creation_milestone',
        context: 'After role creation is complete',
        variants: [
          `🎯 **Role Creation Complete!**

You now have at least 2 roles:
1. **Admin** (auto-created)
2. **Your Custom Role** (just created)

Next step: Let's invite your first team member to your organization so they can start helping with ESG data collection!`,
        ],
      },

      explain_invite_importance: {
        id: 'explain_invite_importance',
        context: 'When user has not invited any users',
        variants: [
          `## 👥 Invite Your First Team Member

ESG reporting is a team effort! You'll need help from different departments:
- **Finance** - For environmental cost data
- **Human Resources** - For social/labor metrics
- **Operations** - For energy and emissions data
- **Compliance** - For governance and risk data

Let's invite your first team member. They'll receive an invitation email and can start collaborating.`,
        ],
      },

      guide_invite_user_form: {
        id: 'guide_invite_user_form',
        context: 'Guiding user through invite form',
        variants: [
          `## 📧 Send Your First Invitation

Fill in the team member's details:

1. **Email** - Their work email address
2. **First Name & Last Name** - Their name
3. **Job Title** - Their position (optional)
4. **Role** - Assign the role you created

The invitation will be sent immediately. They'll receive an email with a link to join.`,
        ],
      },

      user_invited_success: {
        id: 'user_invited_success',
        context: 'After successfully inviting a user',
        variants: [
          `✅ **Invitation Sent!**

Your team member will receive an email invitation to join ESG Caffe and start collaborating on ESG data collection.`,
        ],
      },

      setup_complete_congratulations: {
        id: 'setup_complete_congratulations',
        context: 'When all setup is complete',
        variants: [
          `🎉 **Congratulations!** You've completed the initial setup:

✅ Created at least one custom role
✅ Invited your first team member

**You're all set!** Now you can start:
- Collecting environmental and social data
- Managing team member access
- Generating ESG reports

Your team member will join once they accept the invitation. Would you like help with anything else?`,
        ],
      },

      suggest_next_steps: {
        id: 'suggest_next_steps',
        context: 'Suggesting what to do next',
        variants: [
          `## 🚀 What's Next?

Now that your team is set up, here are some things you can do:

1. **Collect Data** - Start filling in environmental and social metrics
2. **Invite More Team Members** - Add more people to specific roles
3. **Set Data Collection Timeline** - Define deadlines for data submission
4. **View Dashboards** - Monitor your ESG metrics in real-time

I'm here to help with any of these!`,
        ],
      },

      ready_to_help_with_more: {
        id: 'ready_to_help_with_more',
        context: 'Ready to help with more tasks',
        variants: [
          `I'm ready to assist you with anything else! Whether it's data collection, team management, or generating reports, just ask. 😊`,
        ],
      },
    },
  },
};
